import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { api } from "../../api/client";

type LbRow = { rank: number; tg: number; name: string; friends: number };
type ChainNode = { tg: number; name: string; parentTg: number; depth: number; children: number };
type Config = { channel_id: string; channel_name: string; bot_token_key: string; discussion_chat_id: string };

const wrap: CSSProperties = { padding: "10px 12px 26px", display: "grid", gap: 12 };
const card: CSSProperties = { border: "1px solid var(--surface-3)", borderRadius: 12, background: "var(--surface-1)", padding: 12 };
const h2: CSSProperties = { margin: "0 0 8px", fontSize: 16, color: "var(--text-1)" };
const muted: CSSProperties = { margin: "0 0 10px", color: "var(--text-2)", fontSize: 12, lineHeight: 1.45 };
const inp: CSSProperties = { width: "100%", minHeight: 36, border: "1px solid var(--surface-3)", borderRadius: 8, background: "var(--surface-0)", color: "var(--text-1)", padding: "0 10px" };
const btn: CSSProperties = { minHeight: 34, border: "1px solid var(--role)", borderRadius: 8, background: "var(--role)", color: "#06121a", fontWeight: 800, cursor: "pointer" };

export function DcCommunitySection({ onToast }: { onToast: (m: string) => void }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState<Config>({ channel_id: "", channel_name: "", bot_token_key: "BOT_TOKEN", discussion_chat_id: "" });
  const [top, setTop] = useState<LbRow[]>([]);
  const [nodes, setNodes] = useState<ChainNode[]>([]);
  const children = useMemo(() => {
    const m = new Map<number, number>();
    for (const n of nodes) m.set(n.parentTg, (m.get(n.parentTg) ?? 0) + 1);
    return m;
  }, [nodes]);
  const load = () => {
    setLoading(true); setError("");
    Promise.all([
      api<Config>("/api/commerce/community-config"),
      api<{ top: LbRow[] }>("/api/commerce/community-leaderboard"),
      api<{ nodes: ChainNode[] }>("/api/commerce/community-chain"),
    ])
      .then(([cfg, lb, chain]) => {
        setConfig(cfg);
        setTop(lb.top ?? []);
        setNodes(chain.nodes ?? []);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Коммьюнити недоступно"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  const save = () => {
    setBusy(true); setError("");
    api<{ ok: boolean; config: Config; webhookRefreshed?: boolean }>("/api/commerce/community-config", { method: "PUT", body: { discussion_chat_id: config.discussion_chat_id } })
      .then((r) => { setConfig({ ...config, ...(r.config ?? {}) }); onToast(r.webhookRefreshed ? "Сохранено. Вебхук переподписан на комментарии ✓" : "Сохранено (вебхук обновить не удалось — проверьте токен бота)"); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Ошибка сохранения"))
      .finally(() => setBusy(false));
  };
  if (loading) return <div style={wrap}>Загрузка…</div>;
  return <div style={wrap}>
    {error ? <div style={{ ...card, borderColor: "var(--danger, #ff6b6b)", color: "var(--danger, #ff6b6b)" }}>{error}</div> : null}
    <section style={card}>
      <h2 style={h2}>Канал и группа обсуждения</h2>
      <p style={muted}>Канал работает на боте из tg_channel_config.bot_token_key. Здесь правим только discussion_chat_id группы обсуждения.</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 8, marginBottom: 8 }}>
        <label><small>channel_id</small><input style={inp} readOnly value={config.channel_id} /></label>
        <label><small>bot_token_key</small><input style={inp} readOnly value={config.bot_token_key} /></label>
      </div>
      <label><small>discussion_chat_id</small><input style={inp} value={config.discussion_chat_id} onChange={(e) => setConfig({ ...config, discussion_chat_id: e.target.value })} placeholder="-100..." /></label>
      <button type="button" style={{ ...btn, marginTop: 10, padding: "0 14px" }} disabled={busy} onClick={save}>{busy ? "Сохраняю…" : "Сохранить"}</button>
    </section>
    <section style={card}>
      <h2 style={h2}>Лидерборд конкурса</h2>
      <div style={{ display: "grid", gap: 6 }}>
        {top.length ? top.map((r) => <div key={r.tg} style={{ display: "grid", gridTemplateColumns: "42px minmax(0,1fr) 80px", gap: 8, alignItems: "center", minHeight: 34, borderBottom: "1px solid var(--surface-3)" }}>
          <b>#{r.rank}</b><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name} <small>({r.tg})</small></span><b>{r.friends}</b>
        </div>) : <p style={muted}>Пока нет активных приглашений.</p>}
      </div>
    </section>
    <section style={card}>
      <h2 style={h2}>Цепочка «кто кого добавил»</h2>
      <div style={{ display: "grid", gap: 4 }}>
        {nodes.length ? nodes.map((n) => <div key={`${n.parentTg}-${n.tg}-${n.depth}`} style={{ paddingLeft: Math.min(48, n.depth * 14), minHeight: 28, display: "grid", gridTemplateColumns: "minmax(0,1fr) 70px", alignItems: "center", borderBottom: "1px solid var(--surface-3)" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{"↳ ".repeat(Math.max(0, n.depth - 1))}{n.name} <small>#{n.tg}</small></span>
          <small>{children.get(n.tg) ?? n.children ?? 0} прямых</small>
        </div>) : <p style={muted}>Дерево пустое.</p>}
      </div>
    </section>
  </div>;
}
